import { useState } from 'react';
import { toast } from 'sonner';

import {
  useCancelCampaign,
  useLaunchCampaign,
  usePauseCampaign,
  useResumeCampaign,
} from './lifecycle-queries';
import type { CampaignRecord } from './queries';

import { humanErrorMessage } from '@/api/errors';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { Button } from '@/components/ui/button';
import { pluralRu } from '@/lib/plural-ru';

export interface LifecycleButtonsProps {
  campaign: CampaignRecord;
  itemsTotal: number;
}

const OFFER_FORMS = { one: 'offer', few: 'offers', many: 'offers' } as const;

export const LifecycleButtons = ({ campaign, itemsTotal }: LifecycleButtonsProps) => {
  const launch = useLaunchCampaign(campaign.id);
  const pause = usePauseCampaign(campaign.id);
  const resume = useResumeCampaign(campaign.id);
  const cancel = useCancelCampaign(campaign.id);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Plan 08-03 — enablement matrix mirrors `CAMPAIGN_STATUS_TRANSITIONS` in
  // backend `campaigns/service.ts`. Launch additionally requires items > 0
  // (server enforces; UI gates the button so the operator sees why).
  const status = campaign.status;
  const canLaunch = status === 'draft' && itemsTotal > 0;
  const canPause = status === 'running';
  const canResume = status === 'paused';
  const canCancel = status === 'running' || status === 'paused';

  const launchTooltip = !canLaunch
    ? status !== 'draft'
      ? 'Доступно только для draft'
      : 'Сначала добавьте элементы'
    : undefined;

  const handleLaunch = (): void => {
    launch.mutate(undefined, {
      onSuccess: () => {
        toast.success('Кампания запущена');
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  const handlePause = (): void => {
    pause.mutate(undefined, {
      onSuccess: () => {
        toast.success('Кампания приостановлена');
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  const handleResume = (): void => {
    resume.mutate(undefined, {
      onSuccess: () => {
        toast.success('Кампания возобновлена');
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  const handleCancel = (): void => {
    cancel.mutate(undefined, {
      onSuccess: (data) => {
        const cancelled = pluralRu(data.activeCancel.cancelled, OFFER_FORMS);
        toast.success(`Отменено: ${cancelled}, ошибок: ${String(data.activeCancel.failed)}`);
        setConfirmOpen(false);
      },
      onError: (err) => {
        toast.error(humanErrorMessage(err));
        setConfirmOpen(false);
      },
    });
  };

  return (
    <>
      <Button
        variant="outline"
        disabled={!canLaunch || launch.isPending}
        title={launchTooltip}
        onClick={handleLaunch}
      >
        Launch
      </Button>
      <Button variant="outline" disabled={!canPause || pause.isPending} onClick={handlePause}>
        Pause
      </Button>
      <Button variant="outline" disabled={!canResume || resume.isPending} onClick={handleResume}>
        Resume
      </Button>
      <Button
        variant="outline"
        disabled={!canCancel || cancel.isPending}
        onClick={() => {
          setConfirmOpen(true);
        }}
      >
        Cancel
      </Button>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Отменить кампанию?"
        description="Будут отозваны активные offers. Действие необратимо для уже отправленных трейдов."
        confirmLabel="Отменить"
        confirmVariant="destructive"
        onConfirm={handleCancel}
        isPending={cancel.isPending}
      />
    </>
  );
};
